import { useState } from 'react';
import { Button } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon } from 'app/agriculture/icons';
import classNames from 'classnames';
import { FilterButtonItem } from './types';
import './FilterButtons.scss';
import { sortButtonItems } from 'app/agriculture/infobox/common/FilterButtons/utils/sort-button-items';
import { filterButtonItems } from 'app/agriculture/infobox/common/FilterButtons/utils/filter-button-items';

interface FilterButtonsProps<T> {
    items: FilterButtonItem<T>[];
    filterIds: T[];
    orderBy?: 'priority' | 'alphabetical' | 'none' | 'both';
    showMoreLimit?: number;
    onChange: (filterIds: T[]) => void;
}

export function FilterButtons<T>({
    items,
    filterIds,
    onChange,
    orderBy = 'none',
    showMoreLimit = -1,
}: FilterButtonsProps<T>) {
    const [shouldFilterButtonItems, setShouldFilterButtonItems] = useState(
        showMoreLimit < items.length,
    );

    const handleClick = (id: T) => {
        const newFilterIds = filterIds.includes(id)
            ? filterIds.filter((i) => id !== i)
            : [...filterIds, id];

        onChange(newFilterIds);
    };

    if (items.length <= 1) {
        return null;
    }

    const sortedButtonItems = sortButtonItems(items, orderBy);
    const sortedAndFilteredButtonItems = shouldFilterButtonItems
        ? filterButtonItems(sortedButtonItems, showMoreLimit)
        : sortedButtonItems;

    return (
        <div className="FilterButtons">
            <label className="FilterButtons__label">Filter by</label>
            <div className="FilterButtons__button-container">
                {sortedAndFilteredButtonItems.map(({ name, id, icon }) => (
                    <Button
                        key={name}
                        className={classNames('FilterButtons__button', {
                            'FilterButtons__button--selected': filterIds.includes(id),
                        })}
                        variant="outlined"
                        startIcon={icon}
                        onClick={() => {
                            handleClick(id);
                        }}
                    >
                        {name}
                    </Button>
                ))}
                {showMoreLimit > 0 && showMoreLimit < items.length && (
                    <Button
                        className="FilterButtons__button"
                        variant="outlined"
                        endIcon={
                            shouldFilterButtonItems ? (
                                <ChevronDownIcon size={16} />
                            ) : (
                                <ChevronUpIcon size={16} />
                            )
                        }
                        onClick={() => {
                            setShouldFilterButtonItems(!shouldFilterButtonItems);
                        }}
                    >
                        {shouldFilterButtonItems ? 'More' : 'Less'}
                    </Button>
                )}
            </div>
        </div>
    );
}
